import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { OrgType } from 'types/orgs'
import { useFeatureFlag } from 'util/featureFlags'
import { PreviewEmailResponse } from './types'

export const shouldOfferCashFlowRegistration = (org?: OrgType | null) =>
  !org?.cashflow_has_active_payment_methods && !org?.cashflow_onboarding_status

const restClientInstance = restClient(window.API_ROOT + '/api')

/** Payload shape for `orgs/:id/cashflow/setup_progress/` (per-step completion / skip). */
type SetupProgressEntry = boolean | { status?: boolean; skipped?: boolean }

export const useGetIsCashFlowVisible = () => {
  return !!useFeatureFlag('cash_flow', 'on')
}

export const useGetCashFlowInvoicingEnabled = () => !!useFeatureFlag('cashflow_invoicing', 'on')

export const useGetCashFlowNavVisible = () => {
  const cashFlowIsVisible = useGetIsCashFlowVisible()
  const permissions = useSelector(permissionsSelectors.getPermissionsSetting)
  const cashflowOnboardingIsSubmitted = useSelector(orgSelectors.getBlueSnapOnboardingIsSubmitted)

  const showCashFlow = useMemo(() => {
    if (!cashFlowIsVisible) return false
    if (!cashflowOnboardingIsSubmitted) return true
    else {
      return (
        permissions?.cashflow_configuration?.view &&
        permissions?.cashflow_nav_configure?.view &&
        permissions?.cashflow_nav_invoices?.view &&
        permissions?.cashflow_nav_projects?.view &&
        permissions?.cashflow_onboarding?.view
      )
    }
  }, [cashFlowIsVisible, cashflowOnboardingIsSubmitted, permissions])

  return showCashFlow
}

/**
 * Onboarding statuses that count as approved for CashFlow PSP operations (switch, manage labels, etc.).
 * Matches backend `PaymentsOnboardingStatusEnum` and `switch_cashflow_psp` eligibility.
 */
export const CASHFLOW_PSP_SWITCH_ELIGIBLE_STATUSES = ['approved', 'conditionally approved'] as const

export const isCashflowOnboardingEligibleForPspSwitch = (status?: string): boolean => {
  if (!status) {
    return false
  }
  return (CASHFLOW_PSP_SWITCH_ELIGIBLE_STATUSES as readonly string[]).includes(status)
}

/** Effective Stripe onboarding status during the post-Connect redirect window (snapshot before org refresh). */
export const getEffectiveStripeOnboardingStatus = (
  snapshot?: { status?: string } | null,
  onboarding?: { status?: string } | null
): string | undefined => snapshot?.status ?? onboarding?.status

/**
 * Action label for a BlueSnap PSP menu entry, given the current onboarding state.
 * Mirrors the status-aware labels used on the SetupWidget Stripe button (start/continue/manage).
 */
export const getBlueSnapMenuActionLabel = (
  onboarding: { status?: string; is_archived?: boolean } | null | undefined
): string => {
  const status = onboarding?.status
  const isArchived = onboarding?.is_archived
  if (!onboarding || isArchived || !status || status === 'created' || status === 'denied') {
    return 'Start BlueSnap application'
  }
  if (isCashflowOnboardingEligibleForPspSwitch(status)) {
    return 'Manage BlueSnap'
  }
  return 'Continue BlueSnap application'
}

export const isStripeConnectOnboardingApproved = (data: { status?: string } | null | undefined): boolean =>
  isCashflowOnboardingEligibleForPspSwitch(data?.status)

const hasStripeCashflowOnboarding = (org: { cashflow_psp_onboardings?: Array<{ psp_integration: string }> } | null | undefined) =>
  Boolean(org?.cashflow_psp_onboardings?.some((onboarding) => onboarding.psp_integration === 'stripe'))

/**
 * Role permission + org-level page flag. Does not require Connect approval.
 */
export const useStripePaymentSettingsBaseAccess = () => {
  const permissions = useSelector(permissionsSelectors.getPermissionsSetting)
  const org = useSelector(orgSelectors.getOrg)
  return useMemo(() => {
    return Boolean(
      permissions?.cashflow_stripe_payment_settings?.view && org?.stripe_payment_settings_page_enabled !== false
    )
  }, [permissions, org?.stripe_payment_settings_page_enabled])
}

/**
 * Whether the org may open the CashFlow Stripe Payment Settings page: {@link useStripePaymentSettingsBaseAccess} plus
 * the org using Cashflow Stripe Connect and Connect onboarding in an approved state.
 */
export const useStripePaymentSettingsAccess = (): { canAccess: boolean; isLoading: boolean } => {
  const org = useSelector(orgSelectors.getOrg)
  const orgId = org?.id
  const isStripePsp = useFeatureFlag('cashflow_stripe_psp', 'on')
  const isSupplierOrg = Boolean(org?.supplier_org?.is_supplier_base_enabled)
  const hasBaseAccess = useStripePaymentSettingsBaseAccess()

  const isCashflowStripeConnectOrg = useMemo(
    () =>
      Boolean(
        org &&
          (hasStripeCashflowOnboarding(org) || isStripePsp || org.cashflow_psp_integration === 'stripe' || isSupplierOrg)
      ),
    [isStripePsp, isSupplierOrg, org]
  )

  const [connectOnboardingApproved, setConnectOnboardingApproved] = useState<boolean | null>(null)

  useEffect(() => {
    if (!orgId || !hasBaseAccess || !isCashflowStripeConnectOrg) {
      setConnectOnboardingApproved(null)
      return
    }
    setConnectOnboardingApproved(null)
    let cancelled = false
    getStripeOnboardingStatus(orgId)
      .then((data) => {
        if (!cancelled) {
          setConnectOnboardingApproved(isStripeConnectOnboardingApproved(data))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConnectOnboardingApproved(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [orgId, hasBaseAccess, isCashflowStripeConnectOrg])

  return useMemo(() => {
    if (!hasBaseAccess || !isCashflowStripeConnectOrg) {
      return { canAccess: false, isLoading: false }
    }
    if (connectOnboardingApproved === null) {
      return { canAccess: false, isLoading: true }
    }
    return { canAccess: connectOnboardingApproved, isLoading: false }
  }, [hasBaseAccess, isCashflowStripeConnectOrg, connectOnboardingApproved])
}

/** True when Cashflow uses Stripe as PSP (struct flag and/or org field). */
export const useIsStripeCashflowOrg = () => {
  const isStripePsp = useFeatureFlag('cashflow_stripe_psp', 'on')
  const org = useSelector(orgSelectors.getOrg)
  return useMemo(
    () => Boolean(isStripePsp) || org?.cashflow_psp_integration === 'stripe',
    [isStripePsp, org?.cashflow_psp_integration]
  )
}

export const previewEmail = (orgId: number, invoiceId: number, template: string) => {
  return new Promise<PreviewEmailResponse>((resolve, reject) => {
    if (invoiceId) {
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/invoices/${invoiceId}/preview_email/?template=${template}`,
      })
        .then((res: { data: PreviewEmailResponse }) => {
          resolve(res?.data)
        })
        .catch((error) => {
          console.error(error.message)
          reject(error)
        })
    }
  })
}

export const previewOrgEmail = (
  orgId: number,
  template: string,
  amount: number,
  recipientFirstName: string,
  projectId?: number
) => {
  return new Promise<PreviewEmailResponse>((resolve, reject) => {
    if (orgId) {
      let url = `orgs/${orgId}/cashflow/org_email_template_preview/?template_name=${template}&amount=${amount}&recipient_first_name=${recipientFirstName}`
      if (projectId) {
        url += `&project_id=${projectId}`
      }
      restClientInstance('CUSTOM_GET', 'custom', { url })
        .then((res: { data: PreviewEmailResponse }) => {
          resolve(res?.data)
        })
        .catch((error) => {
          console.error(error.message)
          reject(error)
        })
    }
  })
}

export const getInternalCcEmails = (orgId: number, projectId: number, template: string) => {
  return new Promise<{ cc_addresses: string[]; bcc_addresses: string[] }>((resolve, reject) => {
    if (orgId && projectId) {
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/email_template_recipients?email_template=${template}`,
      })
        .then((res) => {
          resolve(res?.data)
        })
        .catch((error) => console.error(error.message))
    }
  })
}

export const getPriceAdjustmentAdderValue = async (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  pricePayable: number,
  adderLabel: string
) => {
  if (!orgId || !projectId) {
    return 'Invalid payment data'
  } else {
    try {
      const response = await restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/payments/adder_adjustment_value/`,
        data: {
          system_uuid: systemUuid,
          payment_option_id: paymentOptionId,
          adder_label: adderLabel,
          price_payable: pricePayable,
        },
      })
      return response?.data
    } catch (ex) {
      return undefined
    }
  }
}

export const getCashFlowSetupChecklist = async (orgId: number) => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/setup_checklist/`,
  })
  return response?.data
}

export const getStripeOnboardingUrl = async (orgId: number): Promise<{ url: string }> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_onboarding_url/`,
  })
  return response?.data
}

export type StripeCashflowSupplementalTermsState = {
  has_agreed: boolean
  terms_document_url: string
}

export const getStripeCashflowSupplementalTermsState = async (
  orgId: number
): Promise<StripeCashflowSupplementalTermsState | undefined> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_cashflow_supplemental_terms/`,
  })
  return response?.data
}

export const recordStripeCashflowSupplementalTerms = async (
  orgId: number
): Promise<{ success: boolean } | undefined> => {
  const response = await restClientInstance('CUSTOM_POST', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_cashflow_supplemental_terms/record/`,
    data: {},
  })
  return response?.data
}

export type StripeOnboardingStatusResponse = {
  applied: boolean
  status?: string
}

export const getStripeOnboardingStatus = async (orgId: number): Promise<StripeOnboardingStatusResponse> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_onboarding_status/`,
  })
  return response?.data
}

export const switchCashflowPsp = async (
  orgId: number,
  targetPsp: 'stripe' | 'bluesnap'
): Promise<{
  success: boolean
  retargeted_payment_request_ids: number[]
  milestone_configurations_updated: number
}> => {
  const response = await restClientInstance('CUSTOM_POST', 'custom', {
    url: `orgs/${orgId}/payments/switch_cashflow_psp/`,
    data: { target_psp: targetPsp },
  })
  return response?.data
}

export const getStripeConnectEmbedPublishableKey = async (orgId: number): Promise<{ publishable_key: string }> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_connect_embed_publishable_key/`,
  })
  return response?.data
}

export const createStripeConnectEmbedSession = async (orgId: number): Promise<{ client_secret: string }> => {
  const response = await restClientInstance('CUSTOM_POST', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_connect_embed_session/`,
    data: {},
  })
  return response?.data
}

export type StripeReportedPaymentRecordRow = {
  payment_request_id: number
  stripe_payment_record_id: string
  amount_minor_units: number
  currency: string
  recorded_at: string | null
  summary: string
}

export const getStripeReportedPaymentRecords = async (
  orgId: number
): Promise<{ results: StripeReportedPaymentRecordRow[] }> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_reported_payment_records/`,
  })
  return response?.data
}

export type StripePaymentMethodRow = {
  method_key: string
  /** Server-provided label for the current locale (e.g. en_us). */
  display_name: string
  available: boolean
  overridable: boolean
  preference: string | null
  value: string | null
}

export type StripePaymentMethodConfigurationResponse = {
  configuration_id: string | null
  payment_methods: StripePaymentMethodRow[]
}

export const getStripePaymentMethodConfiguration = async (
  orgId: number
): Promise<StripePaymentMethodConfigurationResponse> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_payment_method_configuration/`,
  })
  return response?.data
}

export const updateStripePaymentMethodPreference = async (
  orgId: number,
  payload: { method_key: string; preference: 'on' | 'off' }
): Promise<StripePaymentMethodConfigurationResponse> => {
  const response = await restClientInstance('CUSTOM_PATCH', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_payment_method_configuration/`,
    data: payload,
  })
  return response?.data
}

export type StripeCustomerSurchargeSettingsResponse = {
  customer_surcharge_percent: string | null
  customer_surcharge_fixed_minor_units: number | null
  updated_fields?: string[]
}

export const getStripeCustomerSurchargeSettings = async (
  orgId: number
): Promise<StripeCustomerSurchargeSettingsResponse> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_customer_surcharge_settings/`,
  })
  return response?.data
}

export const patchStripeCustomerSurchargeSettings = async (
  orgId: number,
  payload: {
    customer_surcharge_percent?: number | null
    customer_surcharge_fixed_minor_units?: number | null
  }
): Promise<StripeCustomerSurchargeSettingsResponse> => {
  const response = await restClientInstance('CUSTOM_PATCH', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_customer_surcharge_settings/`,
    data: payload,
  })
  return response?.data
}

export const patchStripePaymentSettingsPageEnabled = async (
  orgId: number,
  enabled: boolean
): Promise<{ enabled: boolean }> => {
  const response = await restClientInstance('CUSTOM_PATCH', 'custom', {
    url: `orgs/${orgId}/cashflow/stripe_payment_settings_page_enabled/`,
    data: { enabled },
  })
  return response?.data
}

export const updateSetupProgress = async (setupProgress: Record<string, SetupProgressEntry>, orgId: number) => {
  return await restClientInstance('CUSTOM_PUT', 'custom', {
    url: `orgs/${orgId}/cashflow/setup_progress/`,
    data: { setup_progress: setupProgress },
  })
}

export const getLocaleDateStringFormat = (countryIso2: string | undefined) => {
  if (!countryIso2) return 'en-US'
  if (countryIso2 === 'GB' || countryIso2 === 'AU') {
    return 'en-GB'
  } else {
    return 'en-US'
  }
}
